<template>
    <div class="fight-report-duel">
        <div class="fight-report-duel__banner btn-shadow-static">
            {{ $t('journey.fightReportDuel.lastRoundPrefix') }} {{ attackerIsTrainer ? $t('journey.fightReportDuel.yourAttack') : $t('journey.fightReportDuel.yourDefense') }}
        </div>

        <div class="fight-report-duel__row">
            <div class="fight-report-duel__side">
                <button
                    type="button"
                    class="fight-report-duel__skill"
                    :class="{'fight-report-duel__skill--base': !trainerSkillName}"
                    @click="openSkillPopup('trainer')"
                >
                    <template v-if="trainerSkillType">
                        <TypePill :type="trainerSkillType">
                            <v-icon v-if="trainerSkill.spread" size="16" :color="trainerSkillIconColor">mdi-earth</v-icon>
                            <v-icon v-if="trainerSkill.defence" size="16" :color="trainerSkillIconColor">{{ trainerSkill.defence > 1 ? 'mdi-shield-half-full' : 'mdi-laser-pointer' }}</v-icon>
                            {{ trainerSkillName }}
                        </TypePill>
                    </template>
                    <span v-else class="btn-shadow">{{ $t('journey.fightReportDuel.baseMoveLabel') }}</span>
                </button>
                <div v-if="stunTargetSide === 'trainer' && stunAttempted" class="fight-report-duel__stun-tag btn-shadow-static">{{ $t('journey.fightReportDuel.stunTagLabel') }} {{ report.stunned ? '✓' : '✗' }}</div>
                <div v-if="trainerUsedReflect" class="fight-report-duel__reflect-tag btn-shadow-static">{{ $t('journey.fightReportDuel.reflectTagLabel') }}</div>
                <button type="button" class="fight-report-duel__value" @click="openRollPopup('trainer')">
                    🎲 {{ trainerValue }}<span v-if="trainerBonusTotal !== 0" class="fight-report-duel__value-bonus"> / {{ trainerBonusTotal > 0 ? '+' : '' }}{{ trainerBonusTotal }}</span>
                </button>
            </div>
            <div class="fight-report-duel__vs">vs</div>
            <div class="fight-report-duel__side">
                <button
                    type="button"
                    class="fight-report-duel__skill"
                    :class="{'fight-report-duel__skill--base': !opponentSkillName}"
                    @click="openSkillPopup('opponent')"
                >
                    <template v-if="opponentSkillType">
                        <TypePill :type="opponentSkillType">
                            <v-icon v-if="opponentSkill.spread" size="16" :color="opponentSkillIconColor">mdi-earth</v-icon>
                            <v-icon v-if="opponentSkill.defence" size="16" :color="opponentSkillIconColor">{{ opponentSkill.defence > 1 ? 'mdi-shield-half-full' : 'mdi-laser-pointer' }}</v-icon>
                            {{ opponentSkillName }}
                        </TypePill>
                    </template>
                    <span v-else class="btn-shadow">{{ $t('journey.fightReportDuel.baseMoveLabel') }}</span>
                </button>
                <div v-if="stunTargetSide === 'opponent' && stunAttempted" class="fight-report-duel__stun-tag btn-shadow-static">{{ $t('journey.fightReportDuel.stunTagLabel') }} {{ report.stunned ? '✓' : '✗' }}</div>
                <div v-if="opponentUsedReflect" class="fight-report-duel__reflect-tag btn-shadow-static">{{ $t('journey.fightReportDuel.reflectTagLabel') }}</div>
                <button type="button" class="fight-report-duel__value" @click="openRollPopup('opponent')">
                    🎲 {{ opponentValue }}<span v-if="opponentBonusTotal !== 0" class="fight-report-duel__value-bonus"> / {{ opponentBonusTotal > 0 ? '+' : '' }}{{ opponentBonusTotal }}</span>
                </button>
            </div>
        </div>

        <div v-if="primarySide" class="fight-report-duel__primary" :class="'fight-report-duel__primary--lean-' + (primarySide === 'trainer' ? 'left' : 'right')">
            <div class="fight-report-duel__primary-num" :class="primaryValue > 0 ? 'fight-report-duel__primary-num--heal' : ''">{{ primaryValue > 0 ? '+' : '' }}{{ primaryValue }}</div>
            <div v-if="effectivenessLabel || extraEffectTags.length" class="fight-report-duel__effect-row">
                <span v-if="effectivenessLabel" class="fight-report-duel__effective btn-shadow-static" :class="typeMultiplier > 1 ? 'fight-report-duel__effective--weak' : 'fight-report-duel__effective--resist'">{{ effectivenessLabel }}</span>
                <span
                    v-for="tag in extraEffectTags"
                    :key="tag"
                    class="fight-report-duel__effect-tag btn-shadow-static"
                    :class="'fight-report-duel__effect-tag--' + tag"
                >{{ effectTagLabel(tag) }}</span>
            </div>
        </div>

    </div>
</template>

<script>
import TypePill from '@/components/utils/TypePill';
import {TYPE_DARK_THEME} from '@/utils/typeColorEnum';

export default {
    name: 'FightReportDuel',
    components: {TypePill},
    props: {
        fight: {type: Object, required: true},
    },
    computed: {
        report() {
            return this.fight.fightReport2;
        },
        attackerIsTrainer() {
            return this.fight.beforeAttackRound;
        },
        attackerSide() {
            return this.attackerIsTrainer ? 'trainer' : 'opponent';
        },
        trainerPokemonName() {
            return this.fight.trainerPokemon ? this.fight.trainerPokemon.name : '';
        },
        opponentPokemonName() {
            return this.fight.opponentPokemon ? this.fight.opponentPokemon.name : '';
        },
        trainerSkill() {
            return (this.attackerIsTrainer ? this.report.attSkill : this.report.defSkill) || {};
        },
        opponentSkill() {
            return (this.attackerIsTrainer ? this.report.defSkill : this.report.attSkill) || {};
        },
        trainerSkillName() {
            return this.trainerSkill.name || null;
        },
        opponentSkillName() {
            return this.opponentSkill.name || null;
        },
        trainerSkillType() {
            return this.trainerSkill.type || null;
        },
        opponentSkillType() {
            return this.opponentSkill.type || null;
        },
        // v-icon bez explicitniho :color ignoruje TypePill's iconColor (jsou
        // to samostatne ikony primo ve slotu, ne jeji vlastni :icon prop) -
        // stejny Vuetify ambient-default gotcha jako SkillActionBar.vue.
        trainerSkillIconColor() {
            return this.trainerSkillType && TYPE_DARK_THEME[this.trainerSkillType.id] ? 'white' : 'black';
        },
        opponentSkillIconColor() {
            return this.opponentSkillType && TYPE_DARK_THEME[this.opponentSkillType.id] ? 'white' : 'black';
        },
        // Reflect je vzdy defence-only skill (backend cte jen defSkill->effect).
        trainerUsedReflect() {
            return this.trainerSkill.effect === 'REFLECT';
        },
        opponentUsedReflect() {
            return this.opponentSkill.effect === 'REFLECT';
        },
        trainerDice() {
            return (this.attackerIsTrainer ? this.report.attDice : this.report.defDice) || [];
        },
        opponentDice() {
            return (this.attackerIsTrainer ? this.report.defDice : this.report.attDice) || [];
        },
        trainerValue() {
            return this.attackerIsTrainer ? this.report.attValue : this.report.defValue2;
        },
        opponentValue() {
            return this.attackerIsTrainer ? this.report.defValue2 : this.report.attValue;
        },
        trainerFlatBonus() {
            return this.trainerValue - this.sum(this.trainerDice);
        },
        opponentFlatBonus() {
            return this.opponentValue - this.sum(this.opponentDice);
        },
        trainerBonusDice() {
            return this.attackerIsTrainer ? (this.report.attBonusDice || []) : [];
        },
        opponentBonusDice() {
            return this.attackerIsTrainer ? [] : (this.report.attBonusDice || []);
        },
        trainerBonusFlat() {
            return this.attackerIsTrainer ? (this.trainerSkill.dmgBonus || 0) : 0;
        },
        opponentBonusFlat() {
            return !this.attackerIsTrainer ? (this.opponentSkill.dmgBonus || 0) : 0;
        },
        trainerBonusTotal() {
            return this.sum(this.trainerBonusDice) + this.trainerBonusFlat;
        },
        opponentBonusTotal() {
            return this.sum(this.opponentBonusDice) + this.opponentBonusFlat;
        },
        trainerDmg() {
            return this.report.trainerPokemonDmg;
        },
        opponentDmg() {
            return this.report.opponentPokemonDmg;
        },
        typeMultiplier() {
            return this.report.typeDmgMultiplier;
        },
        stunAttempted() {
            return this.report.stunThreshold !== null && this.report.stunThreshold !== undefined;
        },
        stunTargetSide() {
            return this.attackerIsTrainer ? 'opponent' : 'trainer';
        },
        primarySide() {
            if (this.trainerDmg < 0) return 'trainer';
            if (this.opponentDmg < 0) return 'opponent';
            if (this.trainerDmg !== 0) return 'trainer';
            if (this.opponentDmg !== 0) return 'opponent';
            return null;
        },
        primaryValue() {
            if (this.primarySide === 'trainer') return this.trainerDmg;
            if (this.primarySide === 'opponent') return this.opponentDmg;
            return 0;
        },
        effectivenessLabel() {
            if (this.primaryValue >= 0 || this.typeMultiplier === 1) return null;
            return this.typeMultiplier > 1
                ? this.$t('journey.fightReportDuel.superEffectiveLabel')
                : this.$t('journey.fightReportDuel.notVeryEffectiveLabel');
        },
        // Tagy uz spocitane na backendu (FightService::buildRoundLogEntry) -
        // znovupouzite, at se stejna logika neduplikuje na FE.
        currentRoundTags() {
            const log = this.fight.roundLog || [];
            return log.length ? (log[log.length - 1].tags || []) : [];
        },
        extraEffectTags() {
            // 'splash' schvalne vynechano - je to vlastnost utocnikova
            // skillu, na tomhle (obrancove) radku bez kontextu jen matouci.
            return ['heal', 'lifesteal', 'reflect', 'dodge_ineffective'].filter((tag) => this.currentRoundTags.includes(tag));
        },
    },
    methods: {
        effectTagLabel(tag) {
            switch (tag) {
                case 'heal': return this.$t('journey.fightReportDuel.effectTagHeal');
                case 'lifesteal': return this.$t('journey.fightReportDuel.effectTagLifesteal');
                case 'reflect': return this.$t('journey.fightReportDuel.effectTagReflected');
                case 'dodge_ineffective': return this.$t('journey.fightReportDuel.effectTagDodgeIneffective');
                default: return tag.toUpperCase();
            }
        },
        sum(arr) {
            // Kostky z API jsou stringy (Doctrine "simple_array") - bez
            // Number() by "+" konkatenoval retezce misto souctu.
            return (arr || []).reduce((a, b) => a + Number(b), 0);
        },
        // Popup se vykresluje az v rodici (FightReport.vue), mimo
        // .fight-report-scale-wrap - viz jeho poznamka o transform:scale()
        // containing blocku. Tady se jen spocita/emituje payload.
        buildRollPopupPayload(side) {
            const isTrainer = side === 'trainer';
            const notes = [];
            if (this.stunTargetSide === side && this.stunAttempted) {
                const resultWord = this.report.stunned
                    ? this.$t('journey.fightReportDuel.stunSucceededWord')
                    : this.$t('journey.fightReportDuel.stunFailedWord');
                notes.push(this.$t('journey.fightReportDuel.stunRollNote', {
                    roll: this.report.stunAttemptRoll,
                    threshold: this.report.stunThreshold,
                    result: resultWord,
                }));
            }
            if (this.report.defSkill && this.report.defSkill.defence && this.report.defSkill.effect !== 'REFLECT' && this.report.attValue > this.report.defValue2) {
                notes.push(this.$t('journey.fightReportDuel.defenderBlocksPrefix') + this.report.defSkill.name + this.$t('journey.fightReportDuel.defenderBlocksSuffix'));
            }
            const isIneffectiveDefender = this.report.defIneffective && side !== this.attackerSide;
            const name = isTrainer ? this.trainerPokemonName : this.opponentPokemonName;
            const skillName = isTrainer ? this.trainerSkillName : this.opponentSkillName;
            if (isIneffectiveDefender) {
                // Server uz spocital jednu hodnotu za celou obranu
                // (FightReport2::getDefIneffectiveValue()) - zobrazi se
                // rovnou jako total misto matouciho rozpadu skillu, ktery
                // beztak nebyl relevantni (plosny utok dodge obesel).
                notes.push(this.$t('journey.fightReportDuel.dodgeIneffectiveNote'));
                return {
                    label: name + this.$t('journey.fightReportDuel.baseMoveDodgeIneffectiveSuffix'),
                    dice: [this.report.defIneffectiveValue],
                    flatBonus: 0,
                    total: this.report.defIneffectiveValue,
                    bonusDice: [],
                    bonusFlat: 0,
                    notes,
                };
            }
            return {
                label: name + (skillName ? ' — ' + skillName : this.$t('journey.fightReportDuel.baseMoveSuffix')),
                dice: isTrainer ? this.trainerDice : this.opponentDice,
                flatBonus: isTrainer ? this.trainerFlatBonus : this.opponentFlatBonus,
                total: isTrainer ? this.trainerValue : this.opponentValue,
                bonusDice: isTrainer ? this.trainerBonusDice : this.opponentBonusDice,
                bonusFlat: isTrainer ? this.trainerBonusFlat : this.opponentBonusFlat,
                notes,
            };
        },
        openRollPopup(side) {
            this.$emit('open-roll-popup', this.buildRollPopupPayload(side));
        },
        openSkillPopup(side) {
            const skill = side === 'trainer' ? this.trainerSkill : this.opponentSkill;
            if (!skill || !skill.name) {
                return;
            }
            this.$emit('open-skill-popup', skill);
        },
    },
}
</script>

<style scoped>
.fight-report-duel__banner {
    text-align: center;
    border-radius: 6px;
    padding: 3px 0;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.03em;
    margin-bottom: 5px;
    background: linear-gradient(90deg, #B37D00, var(--v-themeOak-base, #FFB300));
    color: #000;
    line-height: 1.3;
}
.fight-report-duel__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 6px;
}
.fight-report-duel__side {
    flex: 1;
    text-align: center;
}
.fight-report-duel__skill {
    display: block;
    width: 100%;
    margin-top: 0;
    font-size: 16px;
    font-weight: 700;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}
.fight-report-duel__skill--base {
    font-weight: 400;
}
.fight-report-duel__skill--base span {
    display: inline-block;
    border-radius: 10px;
    padding: 3px 11px;
    font-size: 13px;
}
/* Stejne barvy jako SkillActionBar.vue's .skill-action-bar__base-move-btn. */
.theme--light .fight-report-duel__skill--base span {
    background: #ffffff;
    color: #000;
}
.theme--dark .fight-report-duel__skill--base span {
    background: #646464;
    color: #fff;
}
.fight-report-duel__stun-tag {
    display: inline-block;
    margin-top: 4px;
    background: var(--v-themeOak-base, #FFB300);
    color: #000;
    border-radius: 5px;
    padding: 0 6px;
    font-size: 9px;
    font-weight: 700;
}
.fight-report-duel__reflect-tag {
    display: inline-block;
    margin-top: 4px;
    background: var(--v-themeMewtwo-base, #7B1FA2);
    color: #fff;
    border-radius: 5px;
    padding: 0 6px;
    font-size: 9px;
    font-weight: 700;
}
.fight-report-duel__value {
    font-family: monospace;
    font-weight: 800;
    border-radius: 6px;
    padding: 2px 12px;
    margin-top: 3px;
    font-size: 17px;
    border: 1px solid transparent;
    cursor: pointer;
}
.theme--light .fight-report-duel__value {
    background: rgba(0, 0, 0, 0.08);
    color: #000;
}
.theme--dark .fight-report-duel__value {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
}
.fight-report-duel__value-bonus {
    font-size: 13px;
    font-weight: 700;
    opacity: 0.75;
}
.fight-report-duel__vs {
    font-size: 11px;
    opacity: 0.5;
    padding-top: 14px;
}
.fight-report-duel__primary {
    text-align: center;
    margin: 6px 0 3px;
    transition: transform 0.15s ease;
}
.fight-report-duel__primary--lean-left {
    transform: translateX(-15%);
}
.fight-report-duel__primary--lean-right {
    transform: translateX(15%);
}
.fight-report-duel__primary-num {
    font-size: 30px;
    font-weight: 900;
    line-height: 1;
    color: var(--v-error-base, #EF5167);
    text-shadow: 0 2px 8px rgba(239, 81, 103, 0.4);
}
.fight-report-duel__primary-num--heal {
    color: var(--v-primary-base, #6AE754);
    text-shadow: 0 2px 8px rgba(106, 231, 84, 0.4);
}
.fight-report-duel__effect-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    margin-top: 3px;
}
.fight-report-duel__effective {
    display: inline-block;
    padding: 2px 12px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.03em;
    color: #000;
}
.fight-report-duel__effective--weak {
    background: var(--v-secondary-base, #21A87A);
}
.fight-report-duel__effective--resist {
    background: var(--v-error-base, #EF5167);
}
.fight-report-duel__effect-tag {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.03em;
    color: #000;
}
.fight-report-duel__effect-tag--heal {
    background: var(--v-primary-base, #6AE754);
}
.fight-report-duel__effect-tag--lifesteal {
    background: var(--v-accent-base, #7BA6EA);
}
.fight-report-duel__effect-tag--reflect {
    background: var(--v-error-base, #EF5167);
}
.fight-report-duel__effect-tag--dodge_ineffective {
    background: #b0b0b0;
}
</style>
